import {
  FC,
  MouseEvent,
  ReactElement,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";

import { DateRangeFilter, DialogMenu, SelectBox } from "@/components";
import { DUTY_ATTRIBUTES, filter_black_icon, filter_icon } from "@/constants";

export const initialFilters = {
  dutyType: "",
  dateRange: {
    startDate: new Date("2000-01-01"),
    endDate: new Date(),
  },
  mismatchFilter: "all",
};

export type Filters = typeof initialFilters;

interface IAdvanceFilters {
  appliedFilters: {
    dutyType: string;
    dateRange?: { startDate: Date; endDate: Date };
    mismatchFilter?: string;
  };
  onFiltersChange: (filters: {
    dutyType: Attribute;
    dateRange: { startDate: Date; endDate: Date };
    mismatchFilter: string;
  }) => void;
}

const AdvanceFilters: FC<IAdvanceFilters> = ({
  appliedFilters,
  onFiltersChange,
}): ReactElement => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [filters, setFilters] = useState<Filters>(initialFilters);
  const [areFilterApplying, setAreFilterApplying] = useState<boolean>(false);

  const appliedFilterLength = useMemo(() => {
    let count = 0;
    if (filters.dutyType) count++;
    if (filters.dateRange.startDate && filters.dateRange.endDate) count++;
    if (filters.mismatchFilter !== "all") count++;
    return count;
  }, [filters]);

  useEffect(() => {
    setFilters({
      dutyType: appliedFilters.dutyType,
      dateRange: appliedFilters.dateRange || initialFilters.dateRange,
      mismatchFilter: appliedFilters.mismatchFilter || initialFilters.mismatchFilter,
    });
  }, [appliedFilters]);

  const openFilterModal = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeFilterModal = () => {
    setAnchorEl(null);
  };

  const handleChangeValue =
    (name: keyof Filters) =>
    (value: string): void => {
      setFilters({
        ...filters,
        [name]: value,
      });
    };

  const handleMismatchFilterChange = (value: string) => {
    setFilters((prev) => ({ ...prev, mismatchFilter: value }));
  };

  const applyFilters = async (): Promise<void> => {
    setAreFilterApplying(true);
    try {
      await onFiltersChange({
        dutyType: filters.dutyType as Attribute,
        dateRange: filters.dateRange,
        mismatchFilter: filters.mismatchFilter,
      });
    } finally {
      setAreFilterApplying(false);
      closeFilterModal();
    }
  };

  const resetFilters = async (): Promise<void> => {
    setFilters(initialFilters);
    await onFiltersChange({
      dutyType: "" as Attribute,
      dateRange: initialFilters.dateRange,
      mismatchFilter: "all",
    });
    closeFilterModal();
  };

  return (
    <>
      <Button
        variant="primary-contained"
        onClick={openFilterModal}
        id="advance-filter-anchor"
        aria-controls={anchorEl ? "advance-filter-menu" : undefined}
        aria-haspopup="true"
        startIcon={<img src={filter_icon} alt="filter-icon" />}
        aria-expanded={anchorEl ? "true" : undefined}
        sx={{
          minWidth: 230,
          flex: {
            xs: 1,
            sm: "initial",
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography variant="body1" color="white" fontWeight={500} px={0.5}>
            Advance Filter
          </Typography>
          {appliedFilterLength > 0 && (
            <Box
              sx={{
                width: 24,
                height: 24,
                background: "#FFFFFF26",
                display: "grid",
                placeContent: "center",
                borderRadius: "50%",
              }}
            >
              <Typography variant="caption" color="white" fontWeight={500}>
                {appliedFilterLength}
              </Typography>
            </Box>
          )}
          <ExpandMore
            sx={{
              color: "white",
              fontSize: 18,
            }}
          />
        </Stack>
      </Button>
      <DialogMenu
        id="advance-filter-menu"
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={closeFilterModal}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          p={2.5}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <img src={filter_black_icon} alt="filter" />
            <Typography
              variant="h5"
              fontSize={18}
              fontWeight={600}
              color="#030712"
            >
              Advance Filter
            </Typography>
            <Box
              sx={{
                width: 24,
                height: 24,
                display: "grid",
                placeContent: "center",
                backgroundColor: "error.main",
                color: "white",
                borderRadius: "50%",
              }}
            >
              <Typography variant="caption" fontWeight={500} color="white">
                {appliedFilterLength}
              </Typography>
            </Box>
          </Stack>

          <Typography
            sx={{
              color: "#116B6F",
              textDecorationLine: "underline",
              cursor: "pointer",
            }}
            variant="body2"
            fontWeight={500}
            onClick={resetFilters}
          >
            Clear All
          </Typography>
        </Stack>
        <Divider />
        <Stack sx={{ p: 2 }} spacing={1}>
          <SelectBox
            label="Duty Paid"
            placeholder="Select Duty Type"
            value={filters.dutyType}
            handleChangeValue={handleChangeValue("dutyType")}
            options={DUTY_ATTRIBUTES}
          />
          <Box sx={{ width: "100%" }}>
            <Typography
              sx={{
                mb: 1,
                color: "#030712",
              }}
              variant="subtitle2"
            >
              Date & Time
            </Typography>
            <DateRangeFilter
              fullWidth
              value={filters.dateRange}
              onSelectDate={({ startDate, endDate }) => {
                setFilters({
                  ...filters,
                  dateRange: {
                    startDate: startDate ?? new Date("2000-01-01"),
                    endDate: endDate ?? new Date(),
                  },
                });
              }}
            />
          </Box>
          <Box>
            <SelectBox
              label="Filter by Mismatch"
              value={filters.mismatchFilter}
              handleChangeValue={handleMismatchFilterChange}
              options={[
                { value: "all", label: "All Entries" },
                { value: "any", label: "Any Mismatch", color: "linear-gradient(135deg, #ffc107 50%, #4caf50 50%)" },
                { value: "origin-export", label: "Origin ≠ Export", color: "#ffc107" },
                { value: "origin-manufacturer", label: "Origin ≠ Manufacturer", color: "#4caf50" },
                { value: "none", label: "No Mismatches", color: "#f44336" },
              ]}
            />
          </Box>
        </Stack>
        <Divider />
        <Stack
          direction="row"
          justifyContent="space-between"
          spacing={1}
          p={2}
          sx={{
            background: "#F7F8F8",
          }}
        >
          <Button
            variant="white-outlined"
            sx={{ flex: 1 }}
            onClick={closeFilterModal}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ flex: 1, gap: 1 }}
            onClick={applyFilters}
            disabled={areFilterApplying}
            startIcon={
              areFilterApplying ? <CircularProgress size={20} /> : null
            }
          >
            {areFilterApplying ? "Applying" : "Apply"}
          </Button>
        </Stack>
      </DialogMenu>
    </>
  );
};

export default AdvanceFilters;
